import { FIREBASE_PATHS } from '../config/firebase';
import { decodeReplay, decodeReplayV2 } from '../replay/decoder';
import { LEGACY_V1_LINE_DECODER } from '../replay/legacy-v1-lines';
import type { FreeSpinReplayV2, LegacyReplayLineDecoder, ReplayStepV2 } from '../replay/schema';
import type { RealtimeDatabasePort } from './firebase';
import { parseJson, type StoragePort } from './storage';
import { PersistentUploadQueue } from './upload-queue';

const REPLAY_CACHE_KEY = 'slot-yugioh-rework:season3:v2:replay-cache';
const REPLAY_QUEUE_KEY = 'slot-yugioh-rework:season3:v2:replay-upload-queue';
const MAX_LOCAL_REPLAYS = 30;

type UnknownRecord = Record<string, unknown>;

function record(value: unknown): UnknownRecord | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value) ? value as UnknownRecord : null;
}

function legacyStepAliases(step: ReplayStepV2): Readonly<Record<string, unknown>> {
  return Object.freeze({
    ...step,
    spinWin: step.spinWinCents / 100,
    cumulativeWin: step.cumulativeWinCents / 100,
    ...(step.potOfGreed ? {
      pogEffect: {
        reel: step.potOfGreed.position.reel,
        row: step.potOfGreed.position.row,
        awardedFreeSpins: step.potOfGreed.awardedFreeSpins,
        transformedToWild: true,
      },
    } : {}),
  });
}

export function buildRemoteReplay(replay: FreeSpinReplayV2, uid: string): Readonly<Record<string, unknown>> {
  const xValue = replay.totalWinCents / replay.betCents;
  return Object.freeze({
    ...replay,
    seasonId: 'season3',
    ownerUid: uid,
    username: replay.name,
    bet: replay.betCents / 100,
    win: replay.totalWinCents / 100,
    xValue: Number(xValue.toFixed(6)),
    timestamp: replay.createdAt,
    visualMode: replay.presentationMode === 'cards' ? 'max' : 'classic',
    steps: replay.steps.map(legacyStepAliases),
  });
}

function sameReplay(left: FreeSpinReplayV2, right: FreeSpinReplayV2): boolean {
  return left.clientEntryId === right.clientEntryId && left.betCents === right.betCents
    && left.totalWinCents === right.totalWinCents && left.createdAt === right.createdAt
    && JSON.stringify(left.steps) === JSON.stringify(right.steps);
}

export interface ReplaySaveResult {
  readonly replay: FreeSpinReplayV2;
  readonly remoteConfirmed: boolean;
  readonly queued: boolean;
}

export interface ReplayRepository {
  list(): readonly FreeSpinReplayV2[];
  get(clientEntryId: string): Promise<FreeSpinReplayV2 | null>;
  save(replay: FreeSpinReplayV2): Promise<ReplaySaveResult>;
  flushPending(): Promise<number>;
}

export class LocalFirstReplayRepository implements ReplayRepository {
  private readonly cache = new Map<string, FreeSpinReplayV2>();
  private readonly queue: PersistentUploadQueue<FreeSpinReplayV2>;

  constructor(
    private readonly storage: StoragePort,
    private readonly remote: RealtimeDatabasePort,
    private readonly legacyLineDecoder: LegacyReplayLineDecoder = LEGACY_V1_LINE_DECODER,
  ) {
    this.restore();
    this.queue = new PersistentUploadQueue(storage, REPLAY_QUEUE_KEY, {
      decode: (value) => {
        const replay = decodeReplayV2(value);
        return replay?.linesComplete ? replay : null;
      },
    });
  }

  list(): readonly FreeSpinReplayV2[] {
    return [...this.cache.values()].sort((left, right) => right.createdAt - left.createdAt);
  }

  async get(clientEntryId: string): Promise<FreeSpinReplayV2 | null> {
    const local = this.cache.get(clientEntryId);
    if (local) return local;
    try {
      const remote = await this.remote.readValue(`${FIREBASE_PATHS.seasonXReplays}/${clientEntryId}`);
      const replay = decodeReplay(remote, clientEntryId, this.legacyLineDecoder);
      if (replay) this.cacheReplay(replay);
      return replay;
    } catch { return null; }
  }

  async save(value: FreeSpinReplayV2): Promise<ReplaySaveResult> {
    const replay = decodeReplayV2(value);
    if (!replay || !replay.linesComplete) throw new TypeError('Invalid or incomplete v2 replay.');
    this.cacheReplay(replay);
    this.queue.enqueue(replay.clientEntryId, replay);
    const remoteConfirmed = await this.uploadOne(replay);
    return Object.freeze({
      replay, remoteConfirmed,
      queued: this.queue.list().some((item) => item.id === replay.clientEntryId),
    });
  }

  async flushPending(): Promise<number> {
    let confirmed = 0;
    for (const item of this.queue.list()) if (await this.uploadOne(item.payload)) confirmed += 1;
    return confirmed;
  }

  private async uploadOne(replay: FreeSpinReplayV2): Promise<boolean> {
    const availability = await this.remote.connect();
    if (availability.status !== 'online' || !availability.uid) return false;
    this.queue.attempted(replay.clientEntryId);
    try {
      const result = await this.remote.createOnly(
        `${FIREBASE_PATHS.seasonXReplays}/${replay.clientEntryId}`,
        buildRemoteReplay(replay, availability.uid),
      );
      let matches = result.status === 'created';
      if (result.status === 'exists') {
        const existing = decodeReplay(result.existingValue, replay.clientEntryId, this.legacyLineDecoder);
        matches = existing !== null && sameReplay(existing, replay);
      }
      if (matches) this.queue.acknowledge(replay.clientEntryId);
      return matches;
    } catch { return false; }
  }

  private restore(): void {
    const envelope = record(parseJson(this.storage.getItem(REPLAY_CACHE_KEY)));
    if (envelope?.version === 2 && Array.isArray(envelope.replays)) {
      for (const value of envelope.replays) {
        const replay = decodeReplayV2(value);
        if (replay) this.cache.set(replay.clientEntryId, replay);
      }
      this.trimAndPersist();
      return;
    }
    this.storage.removeItem(REPLAY_CACHE_KEY);
  }

  private cacheReplay(replay: FreeSpinReplayV2): void {
    this.cache.set(replay.clientEntryId, replay);
    this.trimAndPersist();
  }

  private trimAndPersist(): void {
    const retained = this.list().slice(0, MAX_LOCAL_REPLAYS);
    this.cache.clear();
    for (const replay of retained) this.cache.set(replay.clientEntryId, replay);
    this.storage.setItem(REPLAY_CACHE_KEY, JSON.stringify({ version: 2, replays: retained }));
  }
}
